<template>
    <div class="filters-container">
        <div class="row g-3 align-items-end">
            <div class="col-lg-4 col-md-6">
                <label for="negociosQ" class="form-label fw-semibold">
                    <i class="fas fa-search text-secondary me-1"></i>
                    Buscar
                </label>
                <input
                    id="negociosQ"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="ID, código de ficha, título, dirección, propietario o cliente..."
                    :value="filtros.q"
                    @input="actualizar('q', $event.target.value)"
                    @keyup.enter="$emit('search')"
                />
            </div>

            <div class="col-lg-4 col-md-6">
                <label for="negociosInmobiliaria" class="form-label fw-semibold">
                    <i class="fas fa-building text-secondary me-1"></i>
                    Inmobiliaria
                </label>
                <select
                    id="negociosInmobiliaria"
                    class="form-select form-select-sm"
                    :value="filtros.inmobiliaria_id ?? ''"
                    @change="actualizar('inmobiliaria_id', $event.target.value)"
                >
                    <option value="">Todas</option>
                    <option v-for="inmo in inmobiliarias" :key="inmo.id" :value="inmo.id">
                        {{ inmo.nombre }} ({{ inmo.negocios }})
                    </option>
                </select>
            </div>

            <div class="col-lg-2 col-md-6">
                <label for="negociosDesde" class="form-label fw-semibold">
                    <i class="fas fa-calendar-alt text-secondary me-1"></i>
                    Cierre desde
                </label>
                <input
                    id="negociosDesde"
                    type="date"
                    class="form-control form-control-sm"
                    :value="filtros.fecha_desde"
                    @change="actualizar('fecha_desde', $event.target.value)"
                />
            </div>

            <div class="col-lg-2 col-md-6">
                <label for="negociosHasta" class="form-label fw-semibold">Cierre hasta</label>
                <input
                    id="negociosHasta"
                    type="date"
                    class="form-control form-control-sm"
                    :value="filtros.fecha_hasta"
                    @change="actualizar('fecha_hasta', $event.target.value)"
                />
            </div>

            <div class="col-lg-3 col-md-4">
                <label for="negociosTipo" class="form-label fw-semibold">Tipo</label>
                <select
                    id="negociosTipo"
                    class="form-select form-select-sm"
                    :value="filtros.tipo ?? ''"
                    @change="actualizar('tipo', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option v-for="tipo in tipos" :key="tipo.value" :value="tipo.value">{{ tipo.label }}</option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="negociosEstado" class="form-label fw-semibold">Estado</label>
                <select
                    id="negociosEstado"
                    class="form-select form-select-sm"
                    :value="filtros.estado ?? ''"
                    @change="actualizar('estado', $event.target.value)"
                >
                    <option value="">Todos</option>
                    <option v-for="estado in estados" :key="estado.value" :value="estado.value">{{ estado.label }}</option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="negociosModalidad" class="form-label fw-semibold">Modalidad</label>
                <select
                    id="negociosModalidad"
                    class="form-select form-select-sm"
                    :value="filtros.modalidad ?? ''"
                    @change="actualizar('modalidad', $event.target.value)"
                >
                    <option value="">Todas</option>
                    <option value="inmobiliaria">Inmobiliaria</option>
                    <option value="mls">MLS</option>
                </select>
            </div>

            <div class="col-lg-2 col-md-4">
                <label for="negociosVigencia" class="form-label fw-semibold">Vigencia</label>
                <select
                    id="negociosVigencia"
                    class="form-select form-select-sm"
                    :value="filtros.vigencia ?? ''"
                    title="Para contratos administrados"
                    @change="actualizar('vigencia', $event.target.value)"
                >
                    <option value="">Todas</option>
                    <option v-for="v in vigencias" :key="v.value" :value="v.value">{{ v.label }}</option>
                </select>
            </div>

            <div class="col-lg-3 col-md-8">
                <label for="negociosOrden" class="form-label fw-semibold">
                    <i class="fas fa-sort text-secondary me-1"></i>
                    Ordenar por
                </label>
                <div class="d-flex gap-2">
                    <select
                        id="negociosOrden"
                        class="form-select form-select-sm"
                        :value="filtros.orden ?? 'recientes'"
                        @change="actualizar('orden', $event.target.value === 'recientes' ? '' : $event.target.value)"
                    >
                        <option v-for="o in ordenes" :key="o.value" :value="o.value">{{ o.label }}</option>
                    </select>
                    <select
                        class="form-select form-select-sm direccion-select"
                        aria-label="Dirección"
                        :value="filtros.direccion ?? 'desc'"
                        @change="actualizar('direccion', $event.target.value === 'desc' ? '' : $event.target.value)"
                    >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="actions-row">
            <div class="d-flex gap-2 flex-wrap align-items-center">
                <SoftButton color="primary" size="sm" variant="gradient" @click="$emit('search')">
                    <i class="fas fa-search me-1"></i>
                    Buscar
                </SoftButton>

                <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('clear')">
                    <i class="fas fa-broom me-1"></i>
                    Limpiar
                </SoftButton>

                <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('refresh')">
                    <i class="fas fa-sync-alt me-1"></i>
                    Actualizar
                </SoftButton>

                <span v-if="activeFiltersCount" class="badge bg-primary bg-gradient ms-2">
                    <i class="fas fa-filter me-1"></i>
                    {{ activeFiltersCount }} filtro{{ activeFiltersCount > 1 ? 's' : '' }}
                </span>

                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
import SoftButton from '@/components/SoftButton.vue';
import { TIPOS, ESTADOS, VIGENCIAS, ORDENES } from '../formatoNegocios.js';

export default {
    name: 'FiltersNegocios',
    components: { SoftButton },
    props: {
        filtros: { type: Object, required: true },
        inmobiliarias: { type: Array, default: () => [] }
    },
    emits: ['update:filtros', 'search', 'clear', 'refresh'],
    setup() {
        return { tipos: TIPOS, estados: ESTADOS, vigencias: VIGENCIAS, ordenes: ORDENES };
    },
    computed: {
        // El orden no es un filtro
        activeFiltersCount() {
            return Object.entries(this.filtros)
                .filter(([clave]) => clave !== 'orden' && clave !== 'direccion')
                .filter(([, v]) => v !== null && v !== '').length;
        }
    },
    methods: {
        actualizar(clave, valor) {
            this.$emit('update:filtros', { ...this.filtros, [clave]: valor === '' ? null : valor });
        }
    }
};
</script>

<style lang="scss" scoped>
.filters-container {
    background: #f8f9fa;
    border-radius: 0.75rem;
    padding: 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;

    .form-label {
        margin-bottom: 0.4rem;
        color: #344767;
        font-size: 0.8rem;
    }
}

.direccion-select {
    max-width: 90px;
}

.actions-row {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}
</style>
